import * as React from 'react';
import { useField } from 'formik';
import { RadioGroup, RadioGroupItem } from '../primitives';
import { FormField } from './FormField';

export interface FormRadioOption {
  label: React.ReactNode;
  value: string;
  description?: React.ReactNode;
  disabled?: boolean;
}

export interface FormRadioGroupProps {
  name: string;
  label?: React.ReactNode;
  options: FormRadioOption[];
  help?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

/** Formik-bound radio group built on the design-system Radix `RadioGroup`. */
export const FormRadioGroup: React.FC<FormRadioGroupProps> = ({
  name,
  label,
  options,
  help,
  required,
  disabled,
  className,
}) => {
  const [field, meta, helpers] = useField(name);
  const error = meta.touched ? meta.error : undefined;

  return (
    <FormField label={label} required={required} error={error} help={help} className={className}>
      <RadioGroup
        value={field.value ?? ''}
        onValueChange={(value) => helpers.setValue(value)}
        disabled={disabled}
      >
        {options.map((option) => {
          const optionId = `${name}-${option.value}`;
          return (
            <div key={option.value} className="flex items-start gap-2">
              <RadioGroupItem id={optionId} value={option.value} disabled={option.disabled} className="mt-0.5" />
              <label htmlFor={optionId} className="cursor-pointer select-none">
                <span className="text-sm font-medium text-foreground">{option.label}</span>
                {option.description && (
                  <span className="block text-sm text-muted-foreground">{option.description}</span>
                )}
              </label>
            </div>
          );
        })}
      </RadioGroup>
    </FormField>
  );
};
